// components/settings/OrganizationSettings.jsx
import React, { useState, useEffect } from 'react';
import { Building2, Users, Edit2, Trash2, Save, X, Shield, UserCog, UserPlus } from 'lucide-react';
import organizationService from '../../services/organizationService';
import { useAuth } from '../auth/AuthContext';

const OrganizationSettings = () => {
  const { user } = useAuth();
  const [organization, setOrganization] = useState(null);
  const [users, setUsers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingOrg, setEditingOrg] = useState(false);
  const [orgName, setOrgName] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');

  const isPrincipal = organization?.organization_role === 'principal';

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');
      setShowCreateForm(false);

      const [orgData, usersData, invitationsData] = await Promise.all([
        organizationService.getOrganization(),
        organizationService.getOrganizationUsers(),
        organizationService.getInvitations().catch(() => ({ invitations: [] }))
      ]);

      setOrganization(orgData);
      setOrgName(orgData.name);
      setUsers(usersData.users || []);
      setInvitations(invitationsData.invitations || []);
    } catch (err) {
      setError(err.message || 'Failed to load organization data');
      // If user has no organization, show create form
      if (err.message && err.message.includes('not associated with any organization')) {
        setShowCreateForm(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateOrganization = async () => {
    if (!orgName.trim()) {
      setError('Organization name cannot be empty');
      return;
    }

    try {
      setError('');
      setSuccess('');
      await organizationService.updateOrganization({ name: orgName.trim() });
      setSuccess('Organization name updated successfully');
      setEditingOrg(false);
      await loadData();
    } catch (err) {
      setError(err.message || 'Failed to update organization');
    }
  };

  const handleCancelEdit = () => {
    setOrgName(organization.name);
    setEditingOrg(false);
    setError('');
  };

  const handleUpdateUserRole = async (userId, newRole) => {
    if (!window.confirm(`Are you sure you want to change this user's role to ${newRole}?`)) {
      return;
    }

    try {
      setError('');
      setSuccess('');
      await organizationService.updateUserRole(userId, newRole);
      setSuccess('User role updated successfully');
      await loadData();
    } catch (err) {
      setError(err.message || 'Failed to update user role');
    }
  };

  const handleSendInvitation = async () => {
    if (!inviteEmail.trim()) {
      setError('Email is required');
      return;
    }

    try {
      setError('');
      setSuccess('');
      await organizationService.sendInvitation(inviteEmail.trim(), inviteRole);
      setSuccess('Invitation sent successfully! The user will receive an email with a registration link.');
      setShowInviteForm(false);
      setInviteEmail('');
      setInviteRole('member');
      await loadData();
    } catch (err) {
      setError(err.message || 'Failed to send invitation');
    }
  };

  const handleCancelInvitation = async (invitationId) => {
    if (!window.confirm('Are you sure you want to cancel this invitation?')) {
      return;
    }

    try {
      setError('');
      setSuccess('');
      await organizationService.cancelInvitation(invitationId);
      setSuccess('Invitation cancelled successfully');
      await loadData();
    } catch (err) {
      setError(err.message || 'Failed to cancel invitation');
    }
  };

  const handleRemoveUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to remove ${userName} from the organization?`)) {
      return;
    }

    try {
      setError('');
      setSuccess('');
      await organizationService.removeUser(userId);
      setSuccess('User removed from organization');
      await loadData();
    } catch (err) {
      setError(err.message || 'Failed to remove user');
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'principal':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300';
      case 'admin':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
          <div className="flex items-center space-x-3">
            <Building2 className="w-8 h-8 text-[#7A99D8]" />
            <div>
              <h1 className="text-lg font-bold text-[#3C3C3C] dark:text-gray-200">Organization Settings</h1>
              <p className="text-sm text-gray-600 dark:text-gray-400">Manage your organization and team members</p>
            </div>
          </div>
        </div>
        <div className="px-6 py-6">
          <div className="flex items-center justify-center h-64">
            <div className="text-gray-500 dark:text-gray-400">Loading organization settings...</div>
          </div>
        </div>
      </div>
    );
  }

  // If user has no organization, show create form
  if (showCreateForm) {
    const handleCreateOrganization = async () => {
      if (!orgName.trim()) {
        setError('Organization name cannot be empty');
        return;
      }

      try {
        setError('');
        setSuccess('');
        setLoading(true);
        await organizationService.createOrganization({ name: orgName.trim() });
        setSuccess('Organization created successfully! You are now the principal.');
        // Reload data to show the new organization
        await loadData();
      } catch (err) {
        setError(err.message || 'Failed to create organization');
        setLoading(false);
      }
    };

    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
          <div className="flex items-center space-x-3">
            <Building2 className="w-8 h-8 text-[#7A99D8]" />
            <div>
              <h1 className="text-lg font-bold text-[#3C3C3C] dark:text-gray-200">Organization Settings</h1>
              <p className="text-sm text-gray-600 dark:text-gray-400">Create your organization and become the principal</p>
            </div>
          </div>
        </div>

        <div className="px-6 py-6">

        {error && (
          <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <p className="text-red-800 dark:text-red-200">{error}</p>
          </div>
        )}

        {success && (
          <div className="mb-6 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
            <p className="text-green-800 dark:text-green-200">{success}</p>
          </div>
        )}

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center">
              <Building2 className="h-6 w-6 text-indigo-600 dark:text-indigo-400 mr-3" />
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Create Your Organization
              </h2>
            </div>
          </div>

          <div className="p-6">
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
              <div className="flex items-start">
                <Shield className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5 mr-3" />
                <div>
                  <p className="text-sm text-blue-800 dark:text-blue-200">
                    You don't have an organization yet. Create one to manage your business and invite team members.
                  </p>
                  <p className="text-sm text-blue-700 dark:text-blue-300 mt-2">
                    As the creator, you'll be assigned as the <strong>principal</strong> with full administrative rights.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Organization Name
                </label>
                <input
                  type="text"
                  value={orgName}
                  onChange={(e) => {
                    setOrgName(e.target.value);
                    setError('');
                  }}
                  className="w-full max-w-md px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Enter your organization name"
                />
              </div>
              <button
                onClick={handleCreateOrganization}
                disabled={loading}
                className="flex items-center gap-2 px-6 py-3 bg-indigo-600 dark:bg-indigo-500 text-white rounded-lg hover:bg-indigo-700 dark:hover:bg-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Building2 className="h-5 w-5" />
                {loading ? 'Creating...' : 'Create Organization'}
              </button>
            </div>
          </div>
        </div>
        </div>
      </div>
    );
  }

  // Safety check: if no organization at this point, show error
  if (!organization) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
          <div className="flex items-center space-x-3">
            <Building2 className="w-8 h-8 text-[#7A99D8]" />
            <div>
              <h1 className="text-lg font-bold text-[#3C3C3C] dark:text-gray-200">Organization Settings</h1>
              <p className="text-sm text-gray-600 dark:text-gray-400">Manage your organization and team members</p>
            </div>
          </div>
        </div>
        <div className="px-6 py-6">
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <p className="text-red-800 dark:text-red-200">
              {error || 'Failed to load organization data'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
        <div className="flex items-center space-x-3">
          <Building2 className="w-8 h-8 text-[#7A99D8]" />
          <div>
            <h1 className="text-lg font-bold text-[#3C3C3C] dark:text-gray-200">Organization Settings</h1>
            <p className="text-sm text-gray-600 dark:text-gray-400">Manage your organization details and team members</p>
          </div>
        </div>
      </div>

      <div className="px-6 py-6">

      {error && (
        <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-800 dark:text-red-200">{error}</p>
        </div>
      )}

      {success && (
        <div className="mb-6 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
          <p className="text-green-800 dark:text-green-200">{success}</p>
        </div>
      )}

      {/* Organization Details */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow mb-8">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Building2 className="h-6 w-6 text-indigo-600 dark:text-indigo-400 mr-3" />
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Organization Details
              </h2>
            </div>
            {isPrincipal && !editingOrg && (
              <button
                onClick={() => setEditingOrg(true)}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300"
              >
                <Edit2 className="h-4 w-4" />
                Edit
              </button>
            )}
          </div>
        </div>

        <div className="p-6">
          {editingOrg ? (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Organization Name
                </label>
                <input
                  type="text"
                  value={orgName}
                  onChange={(e) => setOrgName(e.target.value)}
                  className="w-full max-w-md px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Enter organization name"
                />
              </div>
              <div className="flex gap-3">
                <button
                  onClick={handleUpdateOrganization}
                  className="flex items-center gap-2 px-4 py-2 bg-indigo-600 dark:bg-indigo-500 text-white rounded-lg hover:bg-indigo-700 dark:hover:bg-indigo-600"
                >
                  <Save className="h-4 w-4" />
                  Save
                </button>
                <button
                  onClick={handleCancelEdit}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600"
                >
                  <X className="h-4 w-4" />
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <div className="text-sm text-gray-500 dark:text-gray-400">Name</div>
                <div className="mt-1 text-lg font-medium text-gray-900 dark:text-white">
                  {organization.name}
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-500 dark:text-gray-400">Your Role</div>
                <div className="mt-1">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getRoleBadgeColor(organization.organization_role)}`}>
                    {organization.organization_role}
                  </span>
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-500 dark:text-gray-400">Created</div>
                <div className="mt-1 text-gray-900 dark:text-white">
                  {new Date(organization.created_at).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Team Members */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Users className="h-6 w-6 text-indigo-600 dark:text-indigo-400 mr-3" />
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  Team Members
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  {users.length} {users.length === 1 ? 'member' : 'members'}
                </p>
              </div>
            </div>
            {(isPrincipal || organization?.organization_role === 'admin') && !showInviteForm && (
              <button
                onClick={() => setShowInviteForm(true)}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 dark:bg-indigo-500 text-white rounded-lg hover:bg-indigo-700 dark:hover:bg-indigo-600"
              >
                <UserPlus className="h-4 w-4" />
                Send Invitation
              </button>
            )}
          </div>
        </div>

        {/* Send Invitation Form */}
        {showInviteForm && (isPrincipal || organization?.organization_role === 'admin') && (
          <div className="p-6 bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
              Send Invitation
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Enter email address to invite"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  They will receive an email invitation to register with your organization
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Organization Role
                </label>
                <select
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="member">Member</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={handleSendInvitation}
                  className="flex items-center gap-2 px-4 py-2 bg-indigo-600 dark:bg-indigo-500 text-white rounded-lg hover:bg-indigo-700 dark:hover:bg-indigo-600"
                >
                  <UserPlus className="h-4 w-4" />
                  Send Invitation
                </button>
                <button
                  onClick={() => {
                    setShowInviteForm(false);
                    setInviteEmail('');
                    setInviteRole('member');
                    setError('');
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600"
                >
                  <X className="h-4 w-4" />
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Pending Invitations */}
        {(isPrincipal || organization?.organization_role === 'admin') && invitations.filter(inv => inv.status === 'pending').length > 0 && (
          <div className="p-6 bg-yellow-50 dark:bg-yellow-900/10 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-sm font-medium text-yellow-900 dark:text-yellow-200 mb-3">
              Pending Invitations ({invitations.filter(inv => inv.status === 'pending').length})
            </h3>
            <div className="space-y-2">
              {invitations.filter(inv => inv.status === 'pending').map((invitation) => (
                <div key={invitation.id} className="flex items-center justify-between bg-white dark:bg-gray-800 p-3 rounded-lg">
                  <div className="flex-1">
                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                      {invitation.email}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      Invited by {invitation.invited_by} • Role: {invitation.organization_role} •
                      Expires {new Date(invitation.expires_at).toLocaleDateString()}
                    </div>
                  </div>
                  <button
                    onClick={() => handleCancelInvitation(invitation.id)}
                    className="ml-3 text-sm text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
                  >
                    Cancel
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Org Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Usage
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Joined
                </th>
                {isPrincipal && (
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {users.map((member) => (
                <tr key={member.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {member.first_name} {member.last_name}
                        {member.id === user.id && (
                          <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">(You)</span>
                        )}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">{member.email}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">
                      {member.role}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {isPrincipal && member.id !== user.id ? (
                      <select
                        value={member.organization_role}
                        onChange={(e) => handleUpdateUserRole(member.id, e.target.value)}
                        className="text-sm px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      >
                        <option value="member">Member</option>
                        <option value="admin">Admin</option>
                        <option value="principal">Principal</option>
                      </select>
                    ) : (
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${getRoleBadgeColor(member.organization_role)}`}>
                        {member.organization_role}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    <div className="flex flex-col">
                      <span>{member.total_students} students</span>
                      <span>{member.total_classes} classes</span>
                      <span>{member.total_enrollments} enrollments</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {new Date(member.created_at).toLocaleDateString()}
                  </td>
                  {isPrincipal && (
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      {member.id !== user.id && (
                        <button
                          onClick={() => handleRemoveUser(member.id, `${member.first_name} ${member.last_name}`)}
                          className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 flex items-center gap-1 ml-auto"
                        >
                          <Trash2 className="h-4 w-4" />
                          Remove
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {!isPrincipal && (
          <div className="p-6 bg-blue-50 dark:bg-blue-900/20 border-t border-blue-200 dark:border-blue-800">
            <div className="flex items-start">
              <Shield className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5" />
              <div className="ml-3">
                <p className="text-sm text-blue-800 dark:text-blue-200">
                  Only the principal can manage organization settings and team members.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
      </div>
    </div>
  );
};

export default OrganizationSettings;
